import * as React from 'react';
import { z } from 'zod';

import { Permission, Res, TicketLineDTO } from 'core/api';
import { SessionProvider } from 'core/infrastructure';
import { zDecimal, zMonetary } from 'core/infrastructure-next';
import { Currency } from 'core/utils';
import { FormRef } from 'core/ui-next/controls';
import { CustomerDisplay } from 'pos/services';
import * as SalesStore from '../SalesStore';

// Este fichero ya no pinta pantalla. Sólo exporta la funcionalidad del descuento manual de la línea
// (lo que antes era la clase SetManualLineDiscount) migrada a un hook, para que la use setTicketLineDiscountScreen
// en la pestaña "Descuento manual".

// Equivale al ManualDiscount/State de la clase antigua. El esquema sustituye a las reglas de los controles antiguos:
// allowNegative={false} + precision={2} + Constraints.range(0, 100) + required en el porcentaje y
// allowNegative={false} + required en el importe.
export const manualLineDiscountSchema = z.object({
	percentageDiscount: zDecimal({ decimalPlaces: 2, required: true, nonNegative: true })
		.refine(value => value >= 0 && value <= 100, { message: Res.Msg.MSG_VALUE_IS_OUT_OF_RANGE(0, 100) }),
	cashDiscount: zMonetary({ decimalPlaces: Currency.DECIMAL_PLACES, required: true, nonNegative: true })
});

export type ManualLineDiscountState = z.infer<typeof manualLineDiscountSchema>;

export type TicketLineManualDiscountParams = {
	line: TicketLineDTO;
	// El formulario lo monta la pantalla, así que necesitamos su ref para poder validar antes de aplicar (lo que
	// antes hacía el this.form.current!.updateModel() de la clase)
	formRef: React.RefObject<Nullable<FormRef<ManualLineDiscountState>>>;
	// La pantalla nos pasa el close del Modal para poder cerrarla igual que hacía el this.close() de la clase
	close: () => void;
};

export type TicketLineManualDiscount = {
	initialState: ManualLineDiscountState;
	values: ManualLineDiscountState;
	originalPrice: number;
	totalAmount: number;
	appliedDiscount: boolean;
	discountAmount: number;
	negativeAmountError: Nullable<string>;
	canSetPercentageDiscount: boolean;
	canSetCashDiscount: boolean;
	onPercentageDiscountChange: (value: number) => void;
	onCashDiscountChange: (value: number) => void;
	onOK: () => Promise<void>;
};

// Mientras se teclea, el valor del input puede venir como texto (o incluso vacío). Sólo lo usamos para poder ir
// recalculando el importe final en vivo, igual que hacía el onValueChange + forceUpdate de la clase antigua.
function toNumber(value: unknown): number {
	const parsed = typeof value === 'number' ? value : Number(value);
	return isNaN(parsed) ? 0 : parsed;
}

export function useTicketLineManualDiscount({ line, formRef, close }: TicketLineManualDiscountParams): TicketLineManualDiscount {

	// createInitialState de la clase antigua
	const initialState = React.useMemo<ManualLineDiscountState>(() => ({
		cashDiscount: line.CashDiscount,
		percentageDiscount: line.DiscountPercentage
	}), [line.CashDiscount, line.DiscountPercentage]);

	const [values, setValues] = React.useState<ManualLineDiscountState>(initialState);

	// getOriginalPrice de la clase antigua
	const originalPrice = React.useMemo(() => line.Quantity * line.UnitPrice, [line.Quantity, line.UnitPrice]);

	// getTotalAmount de la clase antigua
	const getTotalAmount = React.useCallback(({ cashDiscount, percentageDiscount }: ManualLineDiscountState) => {
		return originalPrice * (1 - percentageDiscount / 100) - cashDiscount;
	}, [originalPrice]);

	// Es el validate de la clase antigua: no se pueden aplicar descuentos que dejen el importe de la línea en negativo
	const getNegativeAmountError = React.useCallback((model: ManualLineDiscountState): Nullable<string> => {
		if (getTotalAmount(model) >= 0)
			return null;

		return Res.Msg.MSG_CANNOT_APPLY_DISCOUNTS_THAT_MAKE_THE_LINE_AMOUNT_NEGATIVE(model.percentageDiscount, model.cashDiscount, Currency.SYMBOL);
	}, [getTotalAmount]);

	const onPercentageDiscountChange = React.useCallback((value: number) => {
		setValues(previous => ({ ...previous, percentageDiscount: toNumber(value) }));
	}, []);

	const onCashDiscountChange = React.useCallback((value: number) => {
		setValues(previous => ({ ...previous, cashDiscount: toNumber(value) }));
	}, []);

	// onOK de la clase antigua
	const onOK = React.useCallback(async () => {

		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		// El validator antiguo impedía aceptar si el importe final quedaba negativo. Mantenemos la misma condición
		// (el mensaje ya se está mostrando debajo de los importes).
		if (getNegativeAmountError(result.value) !== null)
			return;

		await SalesStore.setLineDiscounts(line, result.value.cashDiscount, result.value.percentageDiscount);

		const customerDisplay = CustomerDisplay;
		if (result.value.cashDiscount !== 0)
			await customerDisplay.displayLineCashDiscount(SalesStore.getTicket(), SalesStore.getLineById(line.Id));
		else if (result.value.percentageDiscount !== 0)
			await customerDisplay.displayLineDiscountPercentage(SalesStore.getTicket(), SalesStore.getLineById(line.Id));
		else
			await customerDisplay.displayExistingLine(SalesStore.getTicket(), SalesStore.getLineById(line.Id));

		close();

	}, [close, formRef, getNegativeAmountError, line]);

	const totalAmount = getTotalAmount(values);

	// Se calcula en cada render a partir de lo que hay en los inputs: hay descuento si alguno de los dos no es 0
	const appliedDiscount = values.percentageDiscount !== 0 || values.cashDiscount !== 0;

	return {
		initialState,
		values,
		originalPrice,
		totalAmount,
		appliedDiscount,
		discountAmount: originalPrice - totalAmount,
		negativeAmountError: getNegativeAmountError(values),
		canSetPercentageDiscount: SessionProvider.hasPermission(Permission.Ticket_SetTicketLinePercentageDiscount),
		canSetCashDiscount: SessionProvider.hasPermission(Permission.Ticket_SetTicketLineCashDiscount),
		onPercentageDiscountChange,
		onCashDiscountChange,
		onOK
	};
}